// src/stores/EntryStore.js - Zustand Store for Customer Activity Entries
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import SOCKET_EVENTS from '../services/socket/socketEvents';

// Store creation with selector subscription middleware
const useEntryStore = create(
  subscribeWithSelector((set, get) => ({
    // ================================
    // STATE
    // ================================

    // Entries indexed by customer ID
    // Structure: { customerId: { entries: [], total: 0, loading: false, filter: null } }
    entriesByCustomer: {},

    // Entry types (cached metadata for filtering UI)
    entryTypes: [],
    entryTypesLoading: false,
    entryTypesLoaded: false,

    // Connection state
    isConnected: false,

    // Internal flags
    _initialized: false,

    // ================================
    // SOCKET OPERATIONS
    // ================================

    /**
     * Load entries for a customer (activity timeline)
     * @param {string} customerId - Customer ID
     * @param {object} options - { entryType, limit, offset }
     */
    loadCustomerEntries: (customerId, options = {}) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('EntryStore: Socket service not initialized');
        return;
      }

      if (!customerId) {
        console.error('EntryStore: Customer ID is required');
        return;
      }

      const { entryType = null, limit = 50, offset = 0 } = options;

      console.log('EntryStore: Loading entries for customer:', customerId, options);

      // Set loading state for this customer
      set(state => ({
        entriesByCustomer: {
          ...state.entriesByCustomer,
          [customerId]: {
            ...state.entriesByCustomer[customerId],
            loading: true,
            filter: entryType
          }
        }
      }));

      const requestData = {
        customer_id: customerId,
        limit,
        offset
      };

      if (entryType) {
        requestData.entry_type = entryType;
      }

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_CUSTOMER_ENTRIES, requestData);
    },

    /**
     * Load entry types for filtering UI
     */
    loadEntryTypes: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('EntryStore: Socket service not initialized');
        return;
      }

      // Don't reload if already loaded
      if (get().entryTypesLoaded) {
        console.log('EntryStore: Entry types already loaded, skipping');
        return;
      }

      console.log('EntryStore: Loading entry types');

      set({ entryTypesLoading: true });

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_ENTRY_TYPES, {});
    },

    // ================================
    // UTILITY FUNCTIONS
    // ================================

    /**
     * Get entries for a specific customer
     */
    getEntriesForCustomer: (customerId) => {
      const { entriesByCustomer } = get();
      return entriesByCustomer[customerId]?.entries || [];
    },

    /**
     * Get loading state for a customer
     */
    isLoadingEntriesForCustomer: (customerId) => {
      const { entriesByCustomer } = get();
      return entriesByCustomer[customerId]?.loading || false;
    },

    /**
     * Get total count for a customer
     */
    getTotalEntriesForCustomer: (customerId) => {
      const { entriesByCustomer } = get();
      return entriesByCustomer[customerId]?.total || 0;
    },

    /**
     * Get current filter for a customer
     */
    getFilterForCustomer: (customerId) => {
      const { entriesByCustomer } = get();
      return entriesByCustomer[customerId]?.filter || null;
    },

    /**
     * Clear entries for a customer (e.g., when navigating away)
     */
    clearEntriesForCustomer: (customerId) => {
      set(state => {
        const updated = { ...state.entriesByCustomer };
        delete updated[customerId];
        return { entriesByCustomer: updated };
      });
    },

    // ================================
    // INTERNAL STATE UPDATERS (called by socket listeners)
    // ================================

    _setEntriesLoaded: (data) => {
      // data structure from backend: { entries: [], total: N, limit: N, offset: N }
      // We need to figure out the customer_id - it should be in the request context
      // The backend sends it back in the response
      console.log('EntryStore: Entries loaded:', data);

      // Find which customer this is for by checking the entries
      // Each entry has a customer_id field
      const customerId = data.entries?.[0]?.customer_id;

      if (!customerId && data.entries?.length === 0) {
        // Empty result - we need to track which customer this was for
        // This is handled by the request tracking in loadCustomerEntries
        console.log('EntryStore: Empty entries result, cannot determine customer');
        return;
      }

      if (!customerId) {
        console.warn('EntryStore: No customer_id in entries response');
        return;
      }

      set(state => ({
        entriesByCustomer: {
          ...state.entriesByCustomer,
          [customerId]: {
            entries: data.entries || [],
            total: data.total || 0,
            loading: false,
            filter: state.entriesByCustomer[customerId]?.filter || null
          }
        }
      }));
    },

    _setEntriesLoadedForCustomer: (customerId, data) => {
      // Alternative method when we know the customer ID
      console.log('EntryStore: Entries loaded for customer:', customerId);

      set(state => ({
        entriesByCustomer: {
          ...state.entriesByCustomer,
          [customerId]: {
            entries: data.entries || [],
            total: data.total || 0,
            loading: false,
            filter: state.entriesByCustomer[customerId]?.filter || null
          }
        }
      }));
    },

    _setEntryTypesLoaded: (data) => {
      console.log('EntryStore: Entry types loaded:', data);

      set({
        entryTypes: data.types || [],
        entryTypesLoading: false,
        entryTypesLoaded: true
      });
    },

    _setEntriesLoadFailed: (customerId) => {
      if (!customerId) return;

      set(state => ({
        entriesByCustomer: {
          ...state.entriesByCustomer,
          [customerId]: {
            ...state.entriesByCustomer[customerId],
            loading: false
          }
        }
      }));
    },

    _setEntryTypesLoadFailed: () => {
      set({
        entryTypesLoading: false
      });
    },

    // ================================
    // INTERNAL METHODS
    // ================================

    _getSocketManager: () => {
      return {
        socketManager: typeof window !== 'undefined' ? window.__socketManager : null
      };
    },

    _initialize: (socketManager) => {
      console.log('EntryStore: Initializing with socket manager');

      if (typeof window !== 'undefined') {
        window.__socketManager = socketManager;
      }

      set({
        isConnected: true,
        _initialized: true
      });
    },

    _setConnectionState: (connected) => {
      set({ isConnected: connected });
    },

    _handleAuthReset: () => {
      console.log('EntryStore: Resetting all state due to auth reset');

      set({
        entriesByCustomer: {},
        entryTypes: [],
        entryTypesLoading: false,
        entryTypesLoaded: false,
        isConnected: false,
        _initialized: false
      });
    }
  }))
);

// ================================
// SOCKET EVENT LISTENERS SETUP
// ================================

// Track pending requests to map responses to customers
let pendingEntryRequests = new Map();

export const initializeEntryStoreSocketListeners = (socketManager) => {
  if (!socketManager?.universalHandler) {
    return;
  }

  console.log('EntryStore: Setting up socket event listeners');

  const { universalHandler } = socketManager;

  // Intercept loadCustomerEntries to track pending requests
  const originalLoadCustomerEntries = useEntryStore.getState().loadCustomerEntries;
  useEntryStore.setState({
    loadCustomerEntries: (customerId, options = {}) => {
      // Track this request
      pendingEntryRequests.set('current', customerId);
      originalLoadCustomerEntries(customerId, options);
    }
  });

  // Set up event listeners
  const entryEvents = [
    SOCKET_EVENTS.CUSTOMER_ENTRIES_LOADED,
    SOCKET_EVENTS.ENTRY_TYPES_LOADED,
    SOCKET_EVENTS.GET_CUSTOMER_ENTRIES_FAILED,
    SOCKET_EVENTS.GET_ENTRY_TYPES_FAILED
  ];

  entryEvents.forEach(eventName => {
    universalHandler.on(eventName, (data) => {
      const store = useEntryStore.getState();

      switch (eventName) {
        case SOCKET_EVENTS.CUSTOMER_ENTRIES_LOADED:
          // Try to get customer_id from the entries or from pending request
          const customerId = data.entries?.[0]?.customer_id || pendingEntryRequests.get('current');
          if (customerId) {
            store._setEntriesLoadedForCustomer(customerId, data);
          } else {
            store._setEntriesLoaded(data);
          }
          pendingEntryRequests.delete('current');
          break;

        case SOCKET_EVENTS.ENTRY_TYPES_LOADED:
          store._setEntryTypesLoaded(data);
          break;

        case SOCKET_EVENTS.GET_CUSTOMER_ENTRIES_FAILED:
          const failedCustomerId = pendingEntryRequests.get('current');
          store._setEntriesLoadFailed(failedCustomerId);
          pendingEntryRequests.delete('current');
          console.error('EntryStore: Failed to load entries:', data?.message);
          break;

        case SOCKET_EVENTS.GET_ENTRY_TYPES_FAILED:
          store._setEntryTypesLoadFailed();
          console.error('EntryStore: Failed to load entry types:', data?.message);
          break;
      }
    });
  });

  // Set up auth reset listener
  if (typeof window !== 'undefined') {
    window.addEventListener('auth-state-reset', () => {
      useEntryStore.getState()._handleAuthReset();
      pendingEntryRequests.clear();
    });
  }

  // Initialize the store
  useEntryStore.getState()._initialize(socketManager);

  console.log('EntryStore: Socket event listeners initialized');
};

export default useEntryStore;
